import React from 'react';
import { Row } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import ResourceRequestFormSection from './ResourceRequestFormSection';
import FormTextAreaField from '../../formFields/FormTextAreaField';
import { ApprovalNamePositionFields } from './SharedFormComponents';
import { ResourceRequest } from '../../hooks/incidentResourceRequestHooks';
import { ApprovalStage } from '../utils/ResourceRequestTypes';
import { isStageCompleted } from '../utils/ResourceRequestUtils';
import { useCurrentUserApprovalAuthority } from '../../../../hooks/incident/useCurrentUserApprovalAuthority';

interface GenericApprovalSectionProps {
  stage: ApprovalStage;
  requestData: ResourceRequest | null;
  currentStage: ApprovalStage;
  onApprove: () => void;
  isSubmitting: boolean;
  isCompleted: boolean;
  nameField: keyof ResourceRequest & `${string}name`;
  positionField?: keyof ResourceRequest & `${string}position`;
  commentsField?: keyof ResourceRequest & `${string}comments`;
  signatureField: keyof ResourceRequest & `${string}signature`;
  timestampField: keyof ResourceRequest & `${string}timestamp`;
  children?: React.ReactNode; // For stage-specific additional fields
  showPositionField?: boolean; // Some stages don't have position
}

const GenericApprovalSection: React.FC<GenericApprovalSectionProps> = ({
  stage,
  requestData,
  currentStage,
  onApprove,
  isSubmitting,
  isCompleted,
  nameField,
  positionField,
  commentsField,
  signatureField,
  timestampField,
  children,
  showPositionField = true,
}) => {
  const { register, watch } = useFormContext();
  const stageCompleted = isStageCompleted(stage, requestData);
  const hasApprovalAuthority = useCurrentUserApprovalAuthority();

  return (
    <ResourceRequestFormSection
      stage={stage}
      currentStage={currentStage}
      isCompleted={stageCompleted}
      completedBy={{
        name: requestData?.[nameField],
        position:
          showPositionField && positionField
            ? requestData?.[positionField]
            : undefined,
        signature: requestData?.[signatureField],
        timestamp: requestData?.[timestampField],
      }}
      onApprove={stageCompleted ? undefined : onApprove}
      isSubmitting={isSubmitting}
      hasApprovalAuthority={hasApprovalAuthority}
    >
      {stageCompleted ? (
        <>
          {/* Show comments if available */}
          {commentsField && (
            <Row>
              <div className="col-12">
                <label className="form-label">Comments</label>
                <textarea
                  className="form-control"
                  value={watch(commentsField) || ''}
                  disabled
                  rows={3}
                />
              </div>
            </Row>
          )}

          {/* Show any stage-specific completed fields */}
          {children}
        </>
      ) : (
        <>
          {/* Active approval fields */}
          {showPositionField && positionField ? (
            <ApprovalNamePositionFields
              nameFieldName={nameField}
              positionFieldName={positionField}
              disabled={!hasApprovalAuthority}
            />
          ) : (
            <Row>
              <div className="col-6">
                <label className="form-label">Name</label>
                <input
                  className="form-control"
                  {...register(nameField)}
                  value={watch(nameField) || ''}
                  disabled={!hasApprovalAuthority}
                  readOnly={!hasApprovalAuthority}
                />
              </div>
            </Row>
          )}

          {/* Comments field if applicable */}
          {commentsField && (
            <Row>
              {isCompleted ? (
                <div className="col-12 mt-2">
                  <label className="form-label">Comments</label>
                  <textarea
                    className="form-control"
                    {...register(commentsField)}
                    disabled={isCompleted}
                    rows={3}
                  />
                </div>
              ) : (
                <FormTextAreaField
                  fieldName={commentsField}
                  title="Comments"
                  variant="full"
                  disabled={!hasApprovalAuthority}
                />
              )}
            </Row>
          )}

          {/* Any stage-specific additional fields */}
          {children}
        </>
      )}
    </ResourceRequestFormSection>
  );
};

export default GenericApprovalSection;
